import { useState } from 'react';
import type { SyntheticEvent, MouseEvent } from 'react';
import { Dialog, TextField, Autocomplete, Typography, Box, Divider } from '@mui/material';
import { List, ListItemText, ListItemButton, ListItemIcon } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { alpha, useTheme } from '@mui/material/styles';
import type { FilterOptionsState } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import HistoryIcon from '@mui/icons-material/History';

import { useMenuItems as menuSettings } from 'pages/web/settings/Settings';
import { useMenuItems as menuTimekeeper } from 'pages/web/timekeeper/Timekeeper';
import { useMenuItems as menuUserManagement } from 'pages/web/userManagement/UserManagement';
import { useMenuItems as manuReports } from 'pages/web/customReports/CustomReports';
import { useMenuItems as manuAccruals } from 'pages/web/accruals/Accruals';

interface RouteItem {
    label: string;
    fullpath: string;
    group: string;
}

const useMenuItems = (): RouteItem[] => {
    const { t } = useTranslation();

    const homeItems = [
        { label: t('home.title'), fullpath: '/', group: t('home.title') },
        { label: t('home.quick_access.settings'), fullpath: '/settings', group: t('home.quick_access.settings') },
        { label: t('home.quick_access.timekeeper'), fullpath: '/timekeeper', group: t('home.quick_access.timekeeper') },
        { label: t('home.quick_access.user_management'), fullpath: '/user_management', group: t('home.quick_access.user_management') },
        { label: t('home.quick_access.accruals'), fullpath: '/accruals', group: t('home.quick_access.accruals') },
        { label: t('home.quick_access.custom_reports'), fullpath: '/custom_reports', group: t('home.quick_access.custom_reports') }
    ];

    // Add group information to menu items
    const menuSettingsItems: RouteItem[] = menuSettings().map((item: any) => ({ label: item.label, fullpath: item.routePath || item.fullpath, group: t('home.quick_access.settings') }));
    const menuTimekeeperItems: RouteItem[] = menuTimekeeper().map((item: any) => ({ label: item.label, fullpath: item.routePath || item.fullpath, group: t('home.quick_access.timekeeper') }));
    const menuUserManagementItems: RouteItem[] = menuUserManagement().map((item: any) => ({ label: item.label, fullpath: item.routePath || item.fullpath, group: t('home.quick_access.user_management') }));
    const menuReportsItems: RouteItem[] = manuReports().map((item: any) => ({ label: item.label, fullpath: item.routePath || item.fullpath, group: t('home.quick_access.custom_reports') }));
    const menuAccrualsItems: RouteItem[] = manuAccruals().map((item: any) => ({ label: item.label, fullpath: item.routePath || item.fullpath, group: t('home.quick_access.accruals') }));

    const listado: RouteItem[] = [...homeItems, ...menuSettingsItems, ...menuTimekeeperItems, ...menuUserManagementItems, ...menuReportsItems, ...menuAccrualsItems];

    // Sort items first by group, then by label
    listado.sort((a, b) => {
        if (a.group !== b.group) {
            return a.group.localeCompare(b.group);
        }
        return a.label.localeCompare(b.label);
    });

    return listado;
};

// Custom hook to manage recently visited routes
const useVisitedRoutes = (): [RouteItem[], (route: RouteItem) => void] => {
    const [visitedRoutes, setVisitedRoutes] = useState<RouteItem[]>(() => {
        const savedRoutes = localStorage.getItem('visitedRoutes');
        return savedRoutes ? (JSON.parse(savedRoutes) as RouteItem[]) : [];
    });

    const addRoute = (route: RouteItem) => {
        setVisitedRoutes((prevRoutes) => {
            const routeWithGroup = {
            ...route,
            group: route.group || 'General'
            };

            const newRoutes = [routeWithGroup, ...prevRoutes.filter(r => r.fullpath !== route.fullpath)];
            const limitedRoutes = newRoutes.slice(0, 5);
            localStorage.setItem('visitedRoutes', JSON.stringify(limitedRoutes));
            return limitedRoutes;
        });
    };

    return [visitedRoutes, addRoute];
};


const useFavoriteRoutes = (): [RouteItem[], (route: RouteItem) => void, (route: RouteItem) => boolean] => {
    const [favoriteRoutes, setFavoriteRoutes] = useState<RouteItem[]>(() => {
        const savedFavorites = localStorage.getItem('favoriteRoutes');
        return savedFavorites ? (JSON.parse(savedFavorites) as RouteItem[]) : [];
    });

    const toggleFavorite = (route: RouteItem) => {
        setFavoriteRoutes((prevFavorites) => {
            const isAlreadyFavorite = prevFavorites.some(fav => fav.fullpath === route.fullpath);

            let newFavorites;
            if (isAlreadyFavorite) {
            newFavorites = prevFavorites.filter(fav => fav.fullpath !== route.fullpath);
            } else {
            const routeWithGroup = {
                ...route,
                group: route.group || 'General'
            };
            newFavorites = [...prevFavorites, routeWithGroup];
            }

            localStorage.setItem('favoriteRoutes', JSON.stringify(newFavorites));
            return newFavorites;
        });
    };

    const isFavorite = (route: RouteItem) => {
        return favoriteRoutes.some(fav => fav.fullpath === route.fullpath);
    };

    return [favoriteRoutes, toggleFavorite, isFavorite];
};

interface SearchModalProps {
    open: boolean;
    onClose: () => void;
}

const SearchModal = ({ open, onClose }: SearchModalProps) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState('');
    const [visitedRoutes, addVisitedRoute] = useVisitedRoutes();
    const [favoriteRoutes, toggleFavorite, isFavorite] = useFavoriteRoutes();
    const menuItems = useMenuItems();

    const handleSelect = (_event: SyntheticEvent | null, value: RouteItem | null) => {
        if (value) {
            addVisitedRoute(value);
            navigate(value.fullpath);
            onClose();
        }
    };

    const getGroupForRoute = (route: RouteItem) => {
        const menuItem = menuItems.find(item => item.fullpath === route.fullpath);
        return menuItem?.group || 'General';
    };

    const filterOptions = (options: RouteItem[], state: FilterOptionsState<RouteItem>) => {
        return options.filter(option =>
            option.label.toLowerCase().includes(state.inputValue.toLowerCase())
        );
    };

    const handleFavoriteClick = (e: MouseEvent<HTMLElement>, route: RouteItem) => {
        e.stopPropagation();
        toggleFavorite(route);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden'
                    }
                },
                backdrop: {
                    sx: {
                        backgroundColor: (theme) => alpha(theme.palette.common.black, 0.6),
                        backdropFilter: 'blur(2px)'
                    }
                }
            }}
        >
            <Box sx={{ width: '100%', position: 'relative', borderRadius: 3, overflow: 'hidden' }}>
                <Autocomplete
                    <RouteItem, false, false, false>
                    fullWidth
                    options={menuItems}
                    getOptionLabel={(option) => option.label}
                    onChange={handleSelect}
                    filterOptions={filterOptions}
                    inputValue={searchValue}
                    onInputChange={(_e, newValue) => setSearchValue(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={t('general.commandPalette.placeholder')}
                            variant="outlined"
                            autoFocus
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    startAdornment: (
                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.primary', mr: 1 }}>
                                            <SearchIcon />
                                        </Box>
                                    ),
                                    sx: {
                                        color: 'text.primary',
                                        '& fieldset': { border: 'none' },
                                        '& input::placeholder': {
                                            color: 'text.secondary',
                                            opacity: 1
                                        },
                                        padding: '8px 16px',
                                        height: 60
                                    }
                                }
                            }}
                        />
                    )}
                    groupBy={(option) => option.group}
                    renderGroup={(params) => (
                        <div key={params.key}>
                            {params.index > 0 && <Divider sx={{ borderColor: 'divider' }} />}
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    fontSize: 12,
                                    my: 1,
                                    px: 2,
                                    pt: 1
                                }}
                            >
                                {params.group}
                            </Typography>
                            {params.children}
                        </div>
                    )}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props as typeof props & { key: string };

                        return (
                            <div
                                key={key}
                                {...optionProps}
                                style={{
                                    padding: '0',
                                    margin: '4px 8px',
                                    borderRadius: '8px',
                                    border: `1px solid ${theme.palette.divider}`,
                                    overflow: 'hidden'
                                }}
                            >
                                <Box
                                    sx={{
                                        padding: '8px 12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ListItemText primary={option.label} sx={{ color: 'text.primary' }} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box
                                            component="span"
                                            onClick={(e) => handleFavoriteClick(e, option)}
                                            sx={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                padding: '4px',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                    borderRadius: '50%'
                                                }
                                            }}
                                        >
                                            {isFavorite(option) ? (
                                                <StarIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                                            ) : (
                                                <StarBorderIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                ml: 1,
                                                backgroundColor: 'action.selected',
                                                color: 'text.secondary',
                                                padding: '2px 8px',
                                                borderRadius: 1,
                                                fontSize: 12
                                            }}
                                        >
                                            {option.group}
                                        </Typography>
                                    </Box>
                                </Box>
                            </div>
                        );
                    }}
                    noOptionsText={<Typography color="text.primary">{t('general.commandPalette.noResults')}</Typography>}
                    sx={{
                        width: '100%',
                        '& .MuiAutocomplete-listbox': {
                            backgroundColor: 'background.paper',
                            color: 'text.primary',
                            paddingTop: 1,
                            paddingBottom: 1,
                            '& .MuiAutocomplete-option': {
                                padding: 0,
                                margin: '4px 8px'
                            }
                        }
                    }}
                />
            </Box>

            {/* Recent Section */}
            {visitedRoutes.length > 0 && (
                <Box sx={{ padding: '8px 16px' }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 'bold', textTransform: 'uppercase', fontSize: 12, my: 1 }}>
                        {t('general.commandPalette.sections.recent')}
                    </Typography>
                    <List sx={{ pt: 0 }}>
                        {visitedRoutes.map((route, index) => (
                            <ListItemButton
                                key={index}
                                onClick={() => handleSelect(null, route)}
                                sx={{
                                    padding: '8px 12px',
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    my: 1,
                                    '&:hover': {
                                        backgroundColor: (theme) => alpha(theme.palette.primary.dark, 0.3),
                                        borderColor: (theme) => theme.palette.primary.main
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <HistoryIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                                        </ListItemIcon>
                                        <ListItemText primary={route.label} sx={{ color: 'text.primary' }} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box
                                            component="span"
                                            onClick={(e) => handleFavoriteClick(e, route)}
                                            sx={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                padding: '4px',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                    borderRadius: '50%'
                                                }
                                            }}
                                        >
                                            {isFavorite(route) ? (
                                                <StarIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                                            ) : (
                                                <StarBorderIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                ml: 1,
                                                backgroundColor: 'action.selected',
                                                color: 'text.secondary',
                                                padding: '2px 8px',
                                                borderRadius: 1,
                                                fontSize: 12
                                            }}
                                        >
                                            {getGroupForRoute(route)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            )}

            {/* Favorites Section */}
            {favoriteRoutes.length > 0 && (
                <Box sx={{ padding: '8px 16px' }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 'bold', textTransform: 'uppercase', fontSize: 12, my: 1 }}>
                        {t('general.commandPalette.sections.favorite')}
                    </Typography>
                    <List sx={{ pt: 0 }}>
                        {favoriteRoutes.map((route, index) => (
                            <ListItemButton
                                key={index}
                                onClick={() => handleSelect(null, route)}
                                sx={{
                                    padding: '8px 12px',
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    my: 1,
                                    '&:hover': {
                                        backgroundColor: (theme) => alpha(theme.palette.primary.dark, 0.3),
                                        borderColor: (theme) => theme.palette.primary.main
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <StarIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                                        </ListItemIcon>
                                        <ListItemText primary={route.label} sx={{ color: 'text.primary' }} />
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            backgroundColor: 'action.selected',
                                            color: 'text.secondary',
                                            padding: '2px 8px',
                                            borderRadius: 1,
                                            fontSize: 12
                                        }}
                                    >
                                        {getGroupForRoute(route)}
                                    </Typography>
                                </Box>
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
            )}

            {/* Footer with Categories */}
            <Divider sx={{ borderColor: 'divider' }}/>
            {/* <Box sx={{ padding: '8px 16px', display: 'flex', justifyContent: 'flex-end', color: 'text.secondary', fontSize: 12, backgroundColor: 'action.selected' }}>
                 <Box component="span" sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                    Search by ‎<Typography sx={{ color: 'primary.main', fontWeight: 'bold', mr: 0.5, ml: 1 }}> GoodRabbit</Typography>
                </Box>
            </Box> */}
        </Dialog>
    );
};

export default SearchModal;